import React, { useState } from "react";
import { motion } from "framer-motion";
import Toast from "../../components/Sweetalert/Sweetalert";
import axios from "axios";
import Logo from "../../assets/image/Logo.png";
import { API } from "../../shared/api/Api";
import Waves from "../../assets/image/wave.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearAuthData, setAuthData } from "../../features/authSlice";
import Loader from "../../components/Live/Loader";
import { setAuthCookies } from "../../utils/cookieUtils";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
  hover: { scale: 1.05 },
  tap: { scale: 0.95 }
};

const waveVariants = {
  animate: {
    y: ["0%", "-5%", "0%"],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const Verification = () => {
  const [code, setCode] = useState("");
  const [loader, setLoader] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    if (!code) {
      Toast.fire({
        text: "Code Field is Required",
        icon: "error",
      });
      return;
    }

    setLoader(true);

    try {
      const endpoint =
        location?.state?.type === 1
          ? API.Users.auth_verify_2fa
          : API.Admin.verify_code;

      const payload = {
        uuid: location?.state?.uuid,
        method: "email",
        ...(location?.state?.type === 1 ? { token: code } : { code }),
      };

      const { data } = await axios.post(endpoint, payload, {
        headers: {
          Accept: "application/json",
          lang: "en",
        },
      });

      if (data.status === 200 && data.errorCode === 0) {
        // dispatch(clearAuthData());
        Toast.fire({
          text: data.message,
          icon: "success",
        });

        // Store in Redux state (existing functionality)
        dispatch(setAuthData(data));

        // Store in secure cookies (new functionality)
        try {
          setAuthCookies(data);
        } catch (cookieError) {
          console.error('Failed to store in cookies:', cookieError);
          // Continue with login even if cookie storage fails
        }

        if (location?.state?.redirect) {
          navigate(location.state.redirect);
        } else {
          navigate("/dashboard/home");
        }
      }
    } catch (err) {
      console.log(err);
      const responseData = err.response.data;

      const errorMessage =
        responseData?.data?.code?.[0] ||
        responseData?.data?.token?.[0] ||
        responseData?.message ||
        "An error occurred.";
      Toast.fire({
        text: errorMessage,
        icon: "warning",
      });
    } finally {
      setLoader(false);
      setCode("");
    }
  };

  const resendCode = async (e) => {
    e.preventDefault();
    setLoader(true);
    try {
      const payload = {
        uuid: location?.state?.uuid,
        type: location?.state?.type,
        method: "email",
      };
      const { data } = await axios.post(API.Admin.reSendCode, payload, {
        headers: {
          Accept: "application/json",
        },
      });
      Toast.fire({
        text: data.message,
        icon: "success",
      });
    } catch (err) {
      Toast.fire({
        text: "Error resending code. Please try again later.",
        icon: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  if (loader) {
    return (
      <div className="mt-[30%] sl:mt-[20%] ll:mt-[15%]">
        <Loader />
      </div>
    );
  }
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="flex flex-1 justify-center items-center mx-auto w-full h-screen p-4 md:p-6 sl:p-8"
    >
      <div className="flex flex-col justify-center items-center mx-auto space-y-4 md:space-y-6 sl:space-y-8 ll:space-y-10">
        <motion.img
          variants={itemVariants}
          className="w-24 h-24 md:w-28 md:h-28 sl:w-32 sl:h-32 ll:w-36 ll:h-36"
          src={Logo}
          alt="logo"
        />

        <motion.h1
          variants={itemVariants}
          className="text-3xl md:text-4xl sl:text-5xl ll:text-6xl font-medium text-primary text-center"
        >
          We've sent you an email
        </motion.h1>

        <motion.span
          variants={itemVariants}
          className="text-lg md:text-xl sl:text-2xl ll:text-3xl font-medium text-primary text-center"
        >
          {location?.state?.type === 1
            ? "Check your inbox for 2-step FA verification"
            : "Check your inbox for verification code"}
        </motion.span>

        <motion.div
          variants={itemVariants}
          className="flex flex-col justify-center items-center mx-auto space-y-6 md:space-y-8 sl:space-y-10 w-full max-w-xs md:max-w-sm sl:max-w-md ll:max-w-lg"
        >
          <motion.input
            variants={itemVariants}
            type="text"
            name="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter your code"
            className="w-full h-input md:h-input-md sl:h-input-lg px-4 md:px-6 sl:px-8 bg-accent rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-base md:text-lg sl:text-xl ll:text-2xl placeholder:text-secondary/70"
            whileFocus={{ scale: 1.02 }}
          />

          <motion.button
            variants={itemVariants}
            type="button"
            onClick={handleSubmit}
            className="w-full h-input md:h-input-md sl:h-input-lg border-2 border-primary text-primary md:text-lg sl:text-xl ll:text-2xl rounded-lg hover:bg-primary hover:text-white transition-colors font-medium"
            whileHover="hover"
            whileTap="tap"
            variants={itemVariants}
          >
            Next
          </motion.button>
        </motion.div>

        <motion.button
          variants={itemVariants}
          className="border-b-2 border-secondary hover:border-primary"
          onClick={resendCode}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="text-base md:text-lg sl:text-xl ll:text-2xl text-secondary hover:text-primary transition-colors">
            Didn't receive it, Resend
          </span>
        </motion.button>
      </div>

      {/* Animated Waves */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 w-full"
        variants={waveVariants}
        animate="animate"
      >
        <img
          src={Waves}
          alt="waves"
          className="w-full h-auto max-h-20 md:max-h-24 sl:max-h-28 ll:max-h-32"
        />
      </motion.div>
    </motion.div>
  );
  // return (
  //   <div className="flex flex-1 justify-center items-center mx-auto w-full h-screen">
  //     <div className="flex flex-col justify-center items-center mx-auto space-y-8">
  //       <img className="w-[149px] h-[149px]" src={Logo} alt="logo" />

  //       <h1 className="text-[40px] font-[500] text-[#134F98]">
  //         We've sent you an email
  //       </h1>
  //       <span className="text-[20px] font-[500] text-[#134F98]">
  //         {location?.state?.type === 1 ? "Check your inbox for 2-step FA verification" : "Check your inbox for verification code"}
  //       </span>
  //       <div className="flex flex-col justify-center items-center mx-auto space-y-12 w-[493px]">
  //         <input
  //           type="text"
  //           name="code"
  //           value={code}
  //           onChange={(e) => setCode(e.target.value)}
  //           placeholder="Enter your code"
  //           className="input-field h-[70px] bg-[#F3F3F3] w-full"
  //         />
  //         <button
  //           type="button"
  //           onClick={handleSubmit}
  //           className="border-2 border-[#134F98] text-[#134F98] text-[24px] py-2 px-4 rounded-lg w-full h-[69px] hover:bg-[#134F98] hover:text-white transition"
  //         >
  //           Next
  //         </button>
  //       </div>

  //       <button className="border-b-2 border-b-[#929292]" onClick={resendCode}>
  //         <span className="text-[24px] text-[#929292] hover:text-[#134F98]">
  //           Didn't receive it, Resend
  //         </span>
  //       </button>
  //     </div>
  //     {/* Waves */}
  //     <div className="absolute bottom-0">
  //       <img src={Waves} alt="waves" />
  //     </div>
  //   </div>
  // );
};

export default Verification;